import { useEffect } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { SelectDropdown } from '@/components/select-dropdown'
import { useAdminAnnouncementsActions } from '../hooks/use-admin-announce-action'
import type { AdminAnnouncement } from '../types/admin-announcement'

type AdminAnnouncementMutateDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow?: AdminAnnouncement | null
}

const formSchema = z.object({
  title: z.string().min(1, '标题不能为空'),
  content: z.string().min(1, '内容不能为空'),
  status: z.string().min(1, '请选择状态'),
  isPinned: z.boolean(),
})

type AnnouncementForm = z.infer<typeof formSchema>

export function AdminAnnouncementMutateDrawer({
  open,
  onOpenChange,
  currentRow,
}: AdminAnnouncementMutateDrawerProps) {
  const isUpdate = !!currentRow
  const actions = useAdminAnnouncementsActions()

  const form = useForm<AnnouncementForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      content: '',
      status: 'draft',
      isPinned: false,
    },
  })

  // 当 currentRow 变化时重置表单
  useEffect(() => {
    if (currentRow) {
      form.reset({
        title: currentRow.title,
        content: currentRow.content,
        status: currentRow.status,
        isPinned: currentRow.isPinned,
      })
    } else {
      form.reset({
        title: '',
        content: '',
        status: 'draft',
        isPinned: false,
      })
    }
  }, [currentRow, form])

  const onSubmit = async (data: AnnouncementForm) => {
    if (isUpdate && currentRow) {
      await actions.updateAnnouncement(currentRow.id, data)
    } else {
      await actions.createAnnouncement(data)
    }
    onOpenChange(false)
    form.reset()
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) {
          form.reset()
        }
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-start'>
          <SheetTitle>{isUpdate ? '编辑' : '创建'}公告</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? '修改公告信息，完成后点击保存。'
              : '填写公告信息，完成后点击保存。'}
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='announcement-form'
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex-1 space-y-6 overflow-y-auto px-4'
          >
            <FormField
              control={form.control}
              name='title'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>标题</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='输入公告标题' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='content'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>内容</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder='输入公告内容'
                      className='min-h-[200px]'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='status'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>状态</FormLabel>
                  <SelectDropdown
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                    placeholder='选择状态'
                    items={[
                      { label: '草稿', value: 'draft' },
                      { label: '已发布', value: 'published' },
                      { label: '已归档', value: 'archived' },
                    ]}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='isPinned'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>置顶</FormLabel>
                    <div className='text-muted-foreground text-sm'>
                      置顶的公告将显示在列表最前面
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>取消</Button>
          </SheetClose>
          <Button
            form='announcement-form'
            type='submit'
            disabled={actions.isCreating || actions.isUpdating}
          >
            {actions.isCreating || actions.isUpdating ? '保存中...' : '保存'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
